
import './App.css'
import AppInitializer from "@/components/AppInitializer";
import  { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/queryClient';

import LoadingSpinner from "./components/ui/loadingSpinner";


// Páginas públicas (carga inmediata - críticas)
import LoginPage from "@/pages/public/auth/LoginPage";
import RegisterPage from "@/pages/public/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/public/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/public/auth/ResetPasswordPage";
import UnauthorizedPage from "@/pages/public/auth/UnauthorizedPage";
import { StorePage } from "@/pages/public/StorePage";

// Páginas con lazy loading (carga bajo demanda)

const AdminDashboardPage = lazy(
  () => import("@/pages/admin/AdminDashboardPage")
);

const AdminDashboardTestPage = lazy(
  () => import("@/pages/admin/AdminDashboardTestPage")
);

const UserManagementPage = lazy(
  () => import("@/pages/admin/UserManagementPage")
);

const ProductManagementPage = lazy(
  () => import("@/pages/admin/ProductManagementPage")
);

const MenuManagementPage = lazy(
  () => import("@/pages/admin/MenuManagementPage")
);

const CheckoutPage = lazy(() =>
  import("@/pages/ecommerce/CheckoutPage").then((module) => ({
    default: module.CheckoutPage,
  }))
);
const OrderConfirmationPage = lazy(() =>
  import("@/pages/ecommerce/OrderConfirmationPage").then((module) => ({
    default: module.OrderConfirmationPage,
  }))
);
const PaymentSuccessPage = lazy(() =>
  import("@/pages/ecommerce/PaymentSuccessPage").then((module) => ({
    default: module.PaymentSuccessPage,
  }))
);
const PaymentErrorPage = lazy(() =>
  import("@/pages/ecommerce/PaymentErrorPage").then((module) => ({
    default: module.PaymentErrorPage,
  }))
);
const PaymentCancelledPage = lazy(() =>
  import("@/pages/ecommerce/PaymentCancelledPage").then((module) => ({
    default: module.PaymentCancelledPage,
  }))
);
const PaymentProcessingPage = lazy(() =>
  import("@/pages/ecommerce/PaymentProcessingPage").then((module) => ({
    default: module.PaymentProcessingPage,
  }))
);

// Páginas protegidas con lazy loading
const ApoderadoDashboardPage = lazy(
  () => import("@/pages/dashboard/ApoderadoDashboardPage")
);
// const ConsumptionHistoryPage = lazy(
//   () => import("@/pages/ConsumptionHistoryPage")
// );
const ConsumptionHistoryPage = lazy(
  () => import("@/pages/history/ConsumptionHistoryPageV2")
);
// const ConsumptionHistoryPageV2 = lazy(
//   () => import("@/pages/ConsumptionHistoryPageV2")
// );
const OrderDetailPage = lazy(() =>
  import("@/pages/ecommerce/OrderDetailPage").then((module) => ({
    default: module.OrderDetailPage,
  }))
);
// const KDSPage = lazy(() =>
//   import("@/pages/delivery/KDSPage").then((module) => ({ default: module.KDSPage }))
// );
const CajeroKDSPage = lazy(() => import("@/pages/pos/CajeroKDSPage"));
const PurchaseListPage = lazy(() => import("@/pages/history/PurchaseListPage"));
const ProfilePage = lazy(() => import("@/pages/dashboard/ProfilePage"));
const POSPage = lazy(() => import("@/pages/pos/POSPage"));
const CajeroModePage = lazy(() => import("@/pages/pos/CajeroModePage"));
// const CajeroAnonymousQRPage = lazy(() => import("@/pages/pos/CajeroAnonymousQRPage"));
const ScanPage = lazy(() =>
  import("@/pages/delivery/ScanPage").then((module) => ({ default: module.ScanPage }))
);
const StudentDashboardPage = lazy(() => import("@/pages/dashboard/StudentDashboardPage"));
const QRDeliveryTestPage = lazy(() => import("@/pages/admin/QRDeliveryTestPage"));

// Componentes de protección
import ProtectedRouteWrapper from "@/components/business/auth/ProtectedRouteWrapper";
// Layouts (carga inmediata - necesarios para routing)
import DashboardLayout from "@/layouts/DashboardLayout";
import PublicLayout from "./layouts/PublicLayout";
import POSLayout from "@/layouts/POSLayout";
import CajeroLayout from "@/layouts/CajeroLayout";
import DeliveryLayout from "@/layouts/DeliveryLayout";
import AdminLayout from "@/layouts/AdminLayout";

// import CajeroQRScannerPage from "./pages/pos/CajeroQRScannerPage";
import TransactionsAdminPage from "./pages/admin/TransactionsAdminPage";
import UserPaymentsPage from "./pages/dashboard/UserPaymentsPage";

function App() {

  return (
    <QueryClientProvider client={queryClient}>
      <AppInitializer>
        <Router>
          <div className="App">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
              {/* Rutas públicas con PublicLayout */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<StorePage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route
                  path="/order-confirmation/:orderId"
                  element={<OrderConfirmationPage />}
                />
                <Route
                  path="/payment/success"
                  element={<PaymentSuccessPage />}
                />
                <Route path="/payment/error" element={<PaymentErrorPage />} />
                <Route
                  path="/payment/cancelled"
                  element={<PaymentCancelledPage />}
                />
              </Route>


              {/* Rutas de pago (sin navbar) */}
              <Route
                path="/payment/processing"
                element={<PaymentProcessingPage />}
              />

              {/* Rutas de autenticación (sin layout específico) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Ruta de perfil (disponible para todos los usuarios autenticados) */}
              <Route
                element={
                  <ProtectedRouteWrapper
                    allowedRoles={[
                      "Admin",
                      "Apoderado",
                      "Cajero",
                      "Estudiante",
                      "Funcionario",
                      "Personal de Entrega",
                      "Becado",
                    ]}
                  />
                }
              >
                <Route element={<DashboardLayout />}>
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/profile/payments" element={<UserPaymentsPage />} />
                </Route>
              </Route>

              {/* Rutas para Apoderado (y Admin) */}
              <Route
                element={
                  <ProtectedRouteWrapper
                    allowedRoles={["Apoderado", "Admin"]}
                  />
                }
              >
                <Route element={<DashboardLayout />}>
                  <Route
                    path="/dashboard"
                    element={<ApoderadoDashboardPage />}
                  />
                  <Route
                    path="/dashboard/history/:childId"
                    element={<ConsumptionHistoryPage />}
                  />
                  {/* <Route
                    path="/dashboard/history-v2/:childId"
                    element={<ConsumptionHistoryPageV2 />}
                  /> */}
                  <Route
                    path="/purchase-lists"
                    element={<PurchaseListPage />}
                  />
                </Route>
              </Route>

              {/* Rutas para detalle de pedidos (compartidas entre Apoderado y Estudiante) */}
              <Route
                // element={
                //   <ProtectedRouteWrapper
                //     allowedRoles={["Apoderado", "Estudiante", "Admin"]}
                //   />
                // }
              >
                <Route element={<DashboardLayout />}>
                  <Route
                    path="/dashboard/orders/:orderId"
                    element={<OrderDetailPage />}
                  />
                </Route>
              </Route>

              {/* Rutas para Cajero (y Admin) */}
              <Route
                element={
                  <ProtectedRouteWrapper allowedRoles={["Cajero", "Admin"]} />
                }
              >
                <Route element={<POSLayout />}>
                  <Route path="/pos" element={<POSPage />} />
                </Route>
                <Route element={<CajeroLayout />}>
                  <Route path="/cajero" element={<CajeroModePage />} />
  
                </Route>
              </Route>

              {/* Rutas para Personal de Entrega (y Admin) */}
              <Route
                element={
                  <ProtectedRouteWrapper
                    allowedRoles={["Personal de Entrega", "Admin"]}
                  />
                }
              >
                <Route element={<DeliveryLayout />}>
                  <Route path="/scan" element={<ScanPage />} />
                  <Route path="/kds" element={<CajeroKDSPage />} />
                </Route>
              </Route>

              {/* Rutas para Estudiante */}
              <Route
                element={
                  <ProtectedRouteWrapper
                    allowedRoles={["Estudiante", "Admin"]}
                  />
                }
              >
                <Route element={<DashboardLayout />}>
                  <Route
                    path="/student/dashboard"
                    element={<StudentDashboardPage />}
                  />
                </Route>
              </Route>

              {/* Ruta de Testing QR Delivery (solo para desarrollo) */}
              <Route
                element={
                  <ProtectedRouteWrapper
                    allowedRoles={[
                      "Admin",
                      "Apoderado",
                      "Cajero",
                      "Estudiante",
                    ]}
                  />
                }
              >
                <Route element={<DashboardLayout />}>
                  <Route
                    path="/qr-delivery-test"
                    element={<QRDeliveryTestPage />}
                  />
                </Route>
              </Route>

              {/* Cajero Routes */}
              {/* <Route
                path="/cajero/qr-scanner"
                element={
                  <ProtectedRouteWrapper allowedRoles={["Cajero", "Admin"]}>
                    <CajeroQRScannerPage />
                  </ProtectedRouteWrapper>
                }
              /> */}
              {/* <Route
                path="/cajero/anonymous-qr"
                element={
                  <ProtectedRouteWrapper allowedRoles={["Cajero", "Admin"]}>
                    <CajeroAnonymousQRPage />
                  </ProtectedRouteWrapper>
                }
              /> */}

              {/* Rutas solo para Admin */}
              <Route
                element={<ProtectedRouteWrapper allowedRoles={["Admin"]} />}
              >
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={<AdminDashboardPage />} />
                  <Route path="/admin/test" element={<AdminDashboardTestPage />} />
                  <Route path="/admin/users" element={<UserManagementPage />} />
                  <Route path="/admin/products" element={<ProductManagementPage />}/>
                  <Route path="/admin/transactions" element={<TransactionsAdminPage />}/>	
                  <Route path="/admin/menus" element={<MenuManagementPage />} />
                </Route>
              </Route>

              {/* Ruta por defecto - redirigir a tienda */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </AppInitializer>
    </QueryClientProvider>
  )
}

export default App
